<template>

    <div class="relative block z-1 bg-gradient-to-r from-clay-800 via-clay-600 to-clay-700 ">

        <div ref="resultsSection" class="z-10 p-4 space-y-3 overflow-y-auto sm:p-6 md:p-20">
            <div class="mb-12 text-center">
                <h1 class="text-4xl font-extrabold tracking-tight text-clay-50 sm:text-5xl md:text-6xl"> Search <span
                        class="tracking-tight text-white font-display">StageAgent</span></h1>
            </div>
            <div
                class="max-w-5xl mx-auto overflow-hidden transition-all transform bg-white divide-y divide-gray-100 shadow-2xl rounded-xl ring-1 ring-black ring-opacity-5">
                <div class="relative">
                    <svg class="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400" viewBox="0 0 20 20"
                        fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd"
                            d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                            clip-rule="evenodd" />
                    </svg>
                    <input v-model="queryText" @input="onQueryTextChanged" type="text"
                        class="w-full h-12 pr-4 text-gray-800 placeholder-gray-400 bg-transparent border-0 pl-11 focus:ring-0 sm:text-sm"
                        placeholder="Search shows, learning modules, characters, themes, articles and more..." role="combobox" aria-expanded="false" aria-controls="options">
                </div>

            </div>

            <div class="max-w-5xl py-2 mx-auto mt-2 text-sm font-thin text-gray-100" v-if="hasSuggestions">
                <span class="mr-3 font-bold">Did you mean:</span>
                <span class="mr-2 underline cursor-pointer hover:no-underline"
                      v-for="suggestion in suggestionsWhenNoResults"
                      @click.prevent="onSuggestionClicked(suggestion)">{{ suggestion }}
                                                            </span>
            </div>
            
            <div class="max-w-5xl mx-auto">
                <div v-if="relatedTags.length > 0" class="py-2 text-sm font-thin text-gray-100">
                    <p class="inline-block mt-2 space-x-3 space-y-3 sm:ml-4 sm:mt-0">
                        Related searches:</p>
                    <div v-for="tag in relatedTags" :key="tag"
                        class="inline-block mt-2 space-x-3 space-y-3 sm:ml-4 sm:mt-0">
                        <div class="flex flex-wrap items-center -m-1">
                            <a @click.prevent="onRelatedTagClicked(tag)" href="#"
                                class="inline-flex px-2 py-1 m-1 mb-2 text-xs font-medium border rounded-full text-clay-800 bg-clay-50 hover:border-clay-900 hover:border hover:text-clay-700 hover:bg-clay-100">
                                <span>{{ tag }}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="mx-auto max-w-7xl">
        <div class="">
            <div class="h-full min-h-screen md:flex bg-clay-50">

                <div class="p-6 mb-0 md:hidden">
                    <div class="px-4 pt-4 pb-5 mx-auto border rounded-lg border-clay-300 bg-clay-100">
                        <h3 class="pb-2 text-sm font-semibold tracking-wide uppercase text-clay-800">
                            Results by Category
                        </h3>
                        <label for="mobile-category-select" class="sr-only">Select a category</label>
                        <select
                                id="mobile-category-select"
                                name="mobile-category-select"
                                v-model="queryCategory"
                                @change="onOtherResultsClicked(queryCategory)"
                                class="block w-full px-4 py-2 mt-1 text-sm border rounded-md border-clay-300 focus:border-clay-500 focus:ring-clay-500 bg-clay-50 text-clay-900"
                        >
                            <option
                                    v-for="result in otherResults"
                                    :key="result.category"
                                    :value="result.category"
                            >
                                {{ result.category }} ({{ isRetrieving ? '...' : result.totalResults }})
                            </option>
                        </select>
                    </div>
                </div>
                

                <div class="hidden md:flex md:shrink-0">
                    <div class="flex flex-col pt-6 pb-12 bg-white w-54">
                        <div class="flex flex-col flex-1 min-h-0">
                            <div class="flex flex-col flex-1 pt-5 pb-4 overflow-y-auto">
                                <div class="flex items-center px-4 shrink-0"></div>
                                <nav aria-label="Sidebar" class="grid grid-cols-2 md:grid-cols-1">
                                    <div v-for="result in otherResults" :key="result.category"
                                        class="space-y-5 text-clay-700 ">
                                        <a @click.prevent="onOtherResultsClicked(result.category)"
                                            :class="result.category === queryCategory ? 'text-clay-900 bg-slate-100 font-bold': ''"
                                            class="flex items-center w-full px-4 py-2 text-xs font-medium capitalize rounded-md md:rounded-l-md sm:text-sm group hover:text-clay-900 hover:bg-slate-100">
                                            <span class="truncate sm:pr-1">{{ result.category }}</span> (<em>{{
                                                isRetrieving ? '...' : result.totalResults
                                                }}</em>)
                                        </a>
                                    </div>

                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex-col flex-1 min-w-0 overflow-visible bg-white md:flex sm:px-6 lg:px- lg:px-12 border-x-1 border-clay-300 md:border-y-0 border-y-1 md:rounded-none rounded-b-4xl">
                    <div class="relative z-0 flex flex-1 px-4 py-8 mb-12 overflow-visible ">
                        <div class="relative z-0 flex-1 pt-6 focus:outline-hidden xl:order-last">
                            <div class="relative inset-0 px-4 pb-6 sm:px-6 md:px-0">
                                <div class="h-full">
                                    <div class="pt-5 pl-4 pr-4 contentful-tab">
                                        <div class="guidesTab md:h-full">
                                            <div
                                                class="mx-auto overflow-hidden transition-all transform bg-white divide-y divide-gray-200 max-w-7xl ">
                                                <div class="px-4 ">
                                                    <div class="block text-center">
                                                        <div class="">
                                                            <h3 class="mb-4 text-2xl leading-6 text-gray-700">
                                                                Search results for "<span
                                                                    class="font-semibold text-clay-600 ">{{
                                                                    queryText
                                                                    }}</span>"</h3>
                                                        </div>

                                                    </div>
                                                </div>
                                                <SkeletonCard class="mt-3" v-if="isRetrieving" card-style="long"
                                                    :num-of-cards=4>

                                                </SkeletonCard>
                                                <div v-else>
                                                    <div class="space-x-3">
                                                        <div class="overflow-hidden bg-white ">

                                                            <div class="px-4 py-5 bg-white sm:px-6">
                                                                <div
                                                                    class="flex flex-wrap items-center justify-between -mt-2 -ml-4 sm:flex-nowrap">
                                                                    <div class="mt-2 ml-4">
                                                                        <h3
                                                                            class="text-xl font-semibold leading-6 text-gray-900">
                                                                            {{ queryCategory }}</h3>
                                                                    </div>
                                                                    <div class="mt-2 ml-4 shrink-0">
                                                                        <a :href="urlForAdvanceSearch"
                                                                            class="relative inline-flex items-center px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-clay-600 hover:bg-clay-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay-600">Advanced
                                                                            search</a>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>


                                                    </div>
                                                    <div class="grid gap-6 py-2 mb-8 text-sm text-gray-800 xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-2"
                                                        id="options">
                                                        <div class="px-6 col-span-full" v-if="searchResponse.totalResults < 1">
                                                            Sorry, there are no results for the term "<em>{{queryText}}</em>" in the <em>{{queryCategory}}</em> category. Please adjust your search query or select a different category.
                                                        </div>
                                                        
                                                        <div v-else v-for="result in results" :key="result.id"
                                                            :class="getClassForCard"
                                                            class="min-w-full mb-4">
                                                            <ShowCard v-if="queryCategory === 'Shows'" :show="result" />

                                                            <CharacterCard v-else-if="queryCategory === 'Characters'"
                                                                :character="result"></CharacterCard>
                                                            <MonologueCardMinimal v-else-if="queryCategory === 'Monologues'"
                                                                :monologue="result"></MonologueCardMinimal>
                                                            <SceneCard v-else-if="queryCategory === 'Scenes'"
                                                                :scene="result"></SceneCard>
                                                            <SongCardMinimal v-else-if="queryCategory === 'Songs'"
                                                                :song="result"></SongCardMinimal>
                                                            <WriterCardMinimal
                                                                v-else-if="queryCategory === 'Playwrights & composers'"
                                                                :writer="result"></WriterCardMinimal>
                                                            <LearningModuleCardMinimal
                                                                v-else-if="queryCategory === 'Learning modules'"
                                                                :guide="result"></LearningModuleCardMinimal>
                                                            <OpportunityCard
                                                                v-else-if="queryCategory === 'Auditions & Jobs'"
                                                                :opportunity="result"></OpportunityCard>
                                                            <TheatreCompanyCard
                                                                v-else-if="queryCategory === 'Companies'"
                                                                :company="result"></TheatreCompanyCard>
                                                            <TalentProfileCard
                                                                v-else-if="queryCategory === 'Talent profiles'"
                                                                :talent="result"></TalentProfileCard>
                                                            <BlogPostCard v-else-if="queryCategory === 'Blog articles'"
                                                                :post="result"></BlogPostCard>
                                                            <QuizCard v-else-if="queryCategory === 'Quizzes'"
                                                                :quiz="result"></QuizCard>
                                                            <CollegeProgramCard
                                                                v-else-if="queryCategory === 'College Programs'"
                                                                :program="result"></CollegeProgramCard>
                                                        </div>

                                                    </div>
                                                    <paginate v-model="searchResponse.currentPage"
                                                              v-if="searchResponse.totalResults > 0"
                                                        :active-class="'border-electric-500 text-electric-600 border-t-2 pt-4 px-4 inline-flex items-center text-sm font-medium'"
                                                        :click-handler="paginationClickHandler"
                                                        :container-class="'border-t border-gray-200 px-4 flex items-center justify-between sm:px-0'"
                                                        :margin-pages="2"
                                                        :next-class="'-mt-px w-0 flex-1 flex justify-end'"
                                                        :next-link-class="'border-t-2 border-transparent pt-4 pl-1 inline-flex items-center text-sm font-medium text-clay-800 hover:text-gray-700 hover:border-clay-500'"
                                                        :next-text="nextHtml"
                                                        :page-class="'border-transparent text-clay-800 hover:text-gray-700 hover:border-clay-500 border-t-2 pt-4 px-4 sm:inline-flex items-center text-sm font-medium hidden'"
                                                        :page-count="getPageCount()" :page-range="3"
                                                        :prev-class="'-mt-px w-0 flex-1 flex'"
                                                        :prev-link-class="'border-t-2 border-transparent pt-4 pr-1 inline-flex items-center text-sm font-medium text-clay-800 hover:text-gray-700 hover:border-clay-500'"
                                                        :prev-text="prevHtml">
                                                    </paginate>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<script lang="ts" setup>
// @ts-ignore
import {computed, onMounted, ref} from "vue";
import {client} from '@/shared';
// @ts-ignore
import Paginate from "vuejs-paginate-next";
import {
    CategoryWithCount,
    SearchGuidesByCategory,
    SearchGuidesByCategoryResponse,
} from "@/dtos";
import ShowCard from "../Guides/Shows/ShowCardMinimal.vue";
import CharacterCard from "../Guides/Characters/CharacterCardMinimal.vue";
import {useDebounceFn} from "@vueuse/core";
import BlogPostCard from "../Blogs/BlogPostCardMinimal.vue";
import TalentProfileCard from "../Talent/TalentProfileCard.vue";
import SceneCard from "../Guides/Scenes/SceneCardMinimal.vue";
import TheatreCompanyCard from "../TheatreCompanies/TheatreCompanyCard.vue";
import OpportunityCard from "../Opportunities/OpportunityCardMinimal.vue";
import LearningModuleCardMinimal from "../LearningModules/LearningModuleCardMinimal.vue";
import QuizCard from "../Quizzes/QuizCard.vue";
import SkeletonCard from "@/main/components/skeletons/SkeletonCard.vue";
import _ from "lodash";
import CollegeProgramCard from "../CollegePrograms/CollegeProgramCard.vue";
import {trackEvent} from '@/shared';
import SongCardMinimal from "../Guides/Songs/SongCardMinimal.vue";
import MonologueCardMinimal from "../Guides/Monologues/MonologueCardMinimal.vue";
import WriterCardMinimal from "../Guides/Writers/WriterCardMinimal.vue";

const resultsSection = ref(null);
let searchResponse = ref<SearchGuidesByCategoryResponse>({});
let results = ref<[]>([]);
let otherResults = ref<Array<CategoryWithCount>>([]);
let relatedTags = ref<string>([]);
let queryText = ref('');
let queryCategory = ref('');
let urlForAdvanceSearch = ref('/shows');
let request = ref<SearchGuidesByCategory>({});
let isRetrieving = ref(false);


onMounted(async () => {
    let urlParams = new URLSearchParams(window.location.search);
    let term = urlParams.get("term") ?? "shakespeare";
    queryText.value = decodeURIComponent(term);
    let category = urlParams.get("category") ?? "Shows"; 
    queryCategory.value = getValidCategoryOrDefault(decodeURIComponent(category));
    await searchGuidesByCategory(true);
});

const searchGuidesByCategory = async (isPageLoad: boolean = false) => {
    if (queryText.value.length >= 3) {

        if (!isPageLoad) {
            resultsSection.value.scrollIntoView({behavior: "smooth"});
        }

        if (isPageLoad) {
            resetPagination();
        }

        isRetrieving.value = true;

        request.value.q = queryText.value;
        request.value.category = queryCategory.value;
        document.title = `${queryText.value} | StageAgent`;

        await client.get<SearchGuidesByCategoryResponse>(new SearchGuidesByCategory(request.value))
            .then(res => {
                searchResponse.value = res;
                queryCategory.value = res.category;
                results.value = res.results;
                otherResults.value = res.categoriesWithCounts;
                relatedTags.value = res.relatedTags;
                suggestionsWhenNoResults.value = res.suggestions;
                urlForAdvanceSearch = otherResults.value
                    .find((r: CategoryWithCount) => r.category === queryCategory.value)
                    .url ?? "/shows";

                isRetrieving.value = false;
            });
        
        trackEvent( 'search', {
            search_term: queryText.value
        }, false);
    }
}

const onQueryTextChanged = useDebounceFn(async () => {
    await searchGuidesByCategory()
}, 1000)

const hasResults = computed(() => {
    return results.value?.length > 0;
})

const suggestionsWhenNoResults = ref<string>([]);
const hasSuggestions = computed(() => {
    return isRetrieving.value === false && queryText?.value.length >= 3 && suggestionsWhenNoResults.value.length > 0;
});

const onSuggestionClicked = async (suggestion: string) => {
    queryText.value = suggestion;
    resetPagination();
    await onQueryTextChanged();
}

const onOtherResultsClicked = async (clickedCategory: string) => {
    queryCategory.value = clickedCategory;
    resetPagination();
    await searchGuidesByCategory();
}

const onRelatedTagClicked = async (clickedTag: string) => {
    queryText.value = clickedTag;
    resetPagination();
    await onQueryTextChanged();
}

const paginationClickHandler = async (pageNum: any) => {
    request.value.currentPage = pageNum;
    await searchGuidesByCategory();
}

const resetPagination = () => {
    request.value.currentPage = 1;
}

const getPageCount = () => {
    return Math.ceil(searchResponse.value.totalResults / 12);
}

let getClassForCard = computed(() => {
    let fourCol = "";
    let threeCol = "";
    let twoCol = "";

    switch (queryCategory.value) {
        
        case 'Shows':
            return fourCol;
        case 'Companies':
        case 'College Programs':
            return threeCol;
        case 'Talent profiles':
        case 'Quizzes':
            return threeCol;
        default:
            return twoCol;
    }
})

const getValidCategoryOrDefault = (incomingCategory: string) => {
    let validCategories = {
        "Shows": ['Shows', 'shows'],
        "Characters": ['Characters', 'characters'],
        "Monologues": ['Monologues', 'monologues'],
        "Scenes": ['Scenes', 'scenes'],
        "Songs": ['Songs', 'songs'],
        "Playwrights & composers": ['Playwrights & composers', 'Writers', 'writers'],
        "Learning modules": [
            'Learning modules',
            'learning modules'],
        "Auditions & Jobs": ['Auditions & Jobs', 'auditions-jobs'],
        "Companies": ['Companies', 'companies', 'theatre companies', 'theatre'],
        "College Programs": ['College programs', 'College Programs', 'college', 'programs'],
        "Talent profiles": ['Talent profiles', 'talent profiles', 'Talent', 'talent'],
        "Blog articles": ['Blog articles', 'blog articles', 'blog', 'articles'],
        "Quizzes": ['Quizzes', 'quizzes'],
    };

    let category = _.findKey(validCategories, function (cat) {
        return cat.includes(incomingCategory)
    });

    return category ?? "Learning modules";
}


let nextHtml = `<span class="hidden md:inline-block">Next</span>
                        <svg class="w-5 h-5 ml-3 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>`;

let prevHtml = `<svg class="w-5 h-5 mr-3 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z" clip-rule="evenodd" />
                        </svg>
                        <span class="hidden md:inline-block">Previous</span>`;


</script>
<style>

</style>